import { FC, useMemo } from 'react'
import Lightbox from 'yet-another-react-lightbox'
import type { GenericSlide, Slide } from 'yet-another-react-lightbox'
import Video from 'yet-another-react-lightbox/plugins/video'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import 'yet-another-react-lightbox/styles.css'

export type MediaItem = {
  mediaUrl: string
  title: string
  type?: string
  id?: number
}

declare module 'yet-another-react-lightbox' {
  interface SlideTypes {
    pdf: SlidePdf
  }
  interface SlidePdf extends GenericSlide {
    type: 'pdf'
    src: string
    title: string
  }
}

type Props = {
  items: MediaItem[]
  open: boolean
  initialIndex?: number
  onClose: () => void
  onIndexChange?: (index: number) => void
}

type LightboxItem = {
  sourceIndex: number
  slide: Slide
}

const isImage = (type?: string) => type?.startsWith('image')
const isVideo = (type?: string) => type?.startsWith('video')
const isPdf = (type?: string) => type?.includes('pdf')

export const supportsMediaLightbox = (type?: string) =>
  isImage(type) || isVideo(type) || isPdf(type)

export const MediaLightbox: FC<Props> = ({
  items,
  open,
  initialIndex = 0,
  onClose,
  onIndexChange,
}) => {
  const lightboxItems = useMemo<LightboxItem[]>(() => {
    return items.reduce<LightboxItem[]>((acc, item, sourceIndex) => {
      if (isImage(item.type)) {
        acc.push({
          sourceIndex,
          slide: {
            src: item.mediaUrl,
            alt: item.title,
          },
        })
      }

      if (isVideo(item.type)) {
        acc.push({
          sourceIndex,
          slide: {
            type: 'video',
            sources: [
              {
                src: item.mediaUrl,
                type: item.type || 'video/mp4',
              },
            ],
          },
        })
      }

      if (isPdf(item.type)) {
        acc.push({
          sourceIndex,
          slide: { type: 'pdf', src: item.mediaUrl, title: item.title },
        })
      }

      return acc
    }, [])
  }, [items])

  const selectedIndex = Math.max(
    lightboxItems.findIndex((item) => item.sourceIndex === initialIndex),
    0
  )

  if (lightboxItems.length === 0) return null

  return (
    <Lightbox
      open={open}
      close={onClose}
      index={selectedIndex}
      slides={lightboxItems.map((item) => item.slide)}
      plugins={[Video, Zoom]}
      carousel={{ finite: lightboxItems.length <= 1, imageFit: 'contain' }}
      video={{
        controls: true,
        playsInline: true,
        preload: 'metadata',
      }}
      zoom={{
        maxZoomPixelRatio: 4,
        scrollToZoom: true,
      }}
      render={{
        slide: ({ slide, rect }) =>
          slide.type === 'pdf' ? (
            <div
              style={{
                width: rect.width,
                height: rect.height,
                boxSizing: 'border-box',
                paddingTop: 64,
              }}
            >
              <object
                data={`${slide.src}#navpanes=0&scrollbar=0`}
                type='application/pdf'
                style={{ width: '100%', height: '100%' }}
                aria-label={slide.title}
              >
                <a href={slide.src} target='_blank' rel='noreferrer'>
                  Abrir {slide.title}
                </a>
              </object>
            </div>
          ) : undefined,
      }}
      on={{
        view: ({ index }) => {
          const sourceIndex = lightboxItems[index]?.sourceIndex
          if (sourceIndex !== undefined) onIndexChange?.(sourceIndex)
        },
      }}
    />
  )
}
